<template>
  <View as="article" align="start" class="event-row">
    <View justify="center" :shrink="0" class="node">
      <View as="i" align="center" justify="center" class="event-badge" :class="`tint-${copy.tone}`" aria-hidden="true">
        <Icon :name="copy.icon" size="extra-small" />
      </View>
    </View>

    <View :grow="1" class="event-line">
      <Text variant="inherit" as="span" class="event-text">
        <b>{{ copy.title }}</b>
        <template v-if="detailText">{{ detailText }}</template>
        <span v-if="quoteText" class="event-quote">{{ quoteText }}</span>
      </Text>
      <Text variant="body-small" color="secondary" as="span" class="event-when" :title="fullTimestamp">{{ time }}</Text>
    </View>
  </View>
</template>

<script setup lang="ts">
import { useDateFormat } from '@vueuse/core';
import { computed } from 'vue';

import { Icon, Text, View } from '@tallo/design-system';

import { TraceEventCopy } from '../traces/trace-event-copy.function';

const props = defineProps<{ copy: TraceEventCopy; createdAt: string }>();

// The dashes carry their own spacing rather than relying on template whitespace. Prettier runs with
// htmlWhitespaceSensitivity "ignore" and reformats these tags freely; Vue then drops a
// whitespace-only text node sitting between two elements, which is what ate the space before the
// dash and left "New lead— added manually".
const detailText = computed(() => (props.copy.detail ? ` — ${props.copy.detail}` : ''));
const quoteText = computed(() => (props.copy.quote ? ` — “${props.copy.quote}”` : ''));

const time = useDateFormat(() => props.createdAt, 'h:mm A');
const fullTimestamp = useDateFormat(() => props.createdAt, 'MMM DD, YYYY h:mm A');
</script>

<style scoped lang="scss">
.event-row {
  position: relative;
  // The node column is a fixed rail the badge centres on; the line takes the rest.
  column-gap: 0.875rem;
  // Roomier than a message row: an event is a beat between messages, so it needs air on both
  // sides to read as one rather than as a caption on the message above or below it. Not much
  // roomier though — the rating bar above already contributes its own allocated height.
  padding: 0.625rem 0;
}

.node {
  position: relative;
  z-index: 1;
  width: 1.75rem;
}

.event-badge {
  width: 1.375rem;
  height: 1.375rem;
  border-radius: var(--t-border-radius-half-rounded);
  // The ring masks the rail line behind the badge.
  box-shadow: 0 0 0 0.1875rem var(--t-color-neutral-white);

  :deep(.svg-icon) {
    width: 0.75rem;
    height: 0.75rem;
  }
}

.tint-orange {
  background: var(--t-color-orange-100);
  color: var(--t-color-orange-700);
}

.tint-green {
  background: var(--t-color-green-100);
  color: var(--t-color-green-600);
}

.tint-blue {
  background: var(--t-color-blue-100);
  color: var(--t-color-blue-600);
}

.tint-red {
  background: var(--t-color-red-100);
  color: var(--t-color-red-700);
}

.tint-amber {
  background: var(--t-color-yellow-100);
  color: var(--t-color-yellow-700);
}

.tint-grey {
  background: var(--t-color-grey-100);
  color: var(--t-color-grey-700);
}

.event-line {
  // Baseline is not one of View's align values, and the gap sits between the sm and md tokens so
  // the title and the time read as one line rather than two columns.
  align-items: baseline;
  gap: 0.5625rem;
  padding-top: 0.0625rem;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: var(--t-color-grey-700);
}

.event-text {
  max-width: 56ch;

  b {
    font-weight: 600;
    color: var(--t-text-color-primary);
  }
}

.event-quote {
  color: var(--t-text-color-secondary);
}

.event-when {
  margin-left: auto;
  flex: none;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
</style>
